import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { OverlayProvider } from 'overlay-kit'
import { useEffect } from 'react'
import { ImpersonationBlocked } from '~/components/admin/impersonation-blocked'
import { AdminMobileNav } from '~/components/layout/admin-mobile-nav'
import { AdminSidebarHeader, AdminSideNav } from '~/components/layout/admin-side-nav'
import { IconRail } from '~/components/layout/icon-rail'
import { ImpersonationFrame } from '~/components/layout/impersonation-frame'
import { Sidebar } from '~/components/layout/sidebar'
import { Spinner } from '~/components/ui/spinner'
import { useIdentitySwitchTeardown, useImpersonationSession } from '~/lib/impersonation'
import { consumeSignOutInProgress, ensureAuth } from '~/lib/router-auth'

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ location }) => {
    const auth = await ensureAuth()
    if (!auth.isAuthenticated) {
      const isSignOut = consumeSignOutInProgress()
      throw redirect({
        search: isSignOut ? {} : { redirect: location.pathname },
        to: '/sign-in',
      })
    }
  },
  component: AdminLayout,
  ssr: false,
})

function AdminLayout() {
  // The staff flag is checked client-side (`beforeLoad` runs outside React so
  // it can't easily read Convex queries), and it is read off the REAL
  // credential via `api.impersonation.current` — never off `currentUser`,
  // which resolves the non-staff TARGET while impersonating and so cannot
  // answer "is the person at the keyboard staff?".
  const { isResolved, isStaffAdmin, session } = useImpersonationSession()
  const switchingIdentity = useIdentitySwitchTeardown()
  const navigate = useNavigate()

  // Only bounce non-staff once the session query has answered AND there is
  // genuinely no session: bouncing while one is open would teleport staff
  // away from the one surface that can end it.
  const shouldBounce = isResolved && session === null && !isStaffAdmin

  useEffect(() => {
    if (shouldBounce) {
      navigate({ replace: true, to: '/home' })
    }
  }, [shouldBounce, navigate])

  // `switchingIdentity` keeps the spinner up between an identity switch
  // resolving and the hard navigation, so the blocked state (on start) or the
  // admin shell (on stop) never flashes in for one render first.
  if (!isResolved || switchingIdentity) {
    return (
      <ImpersonationFrame>
        <div className="flex h-full items-center justify-center bg-canvas">
          <Spinner />
        </div>
      </ImpersonationFrame>
    )
  }

  if (session !== null) {
    return (
      <ImpersonationFrame>
        <div className="h-full bg-canvas">
          <ImpersonationBlocked session={session} />
        </div>
      </ImpersonationFrame>
    )
  }

  if (!isStaffAdmin) {
    return null
  }

  // Mirrors the app shell in `$workspaceSlug.tsx`: grey backdrop, one docked
  // sidebar carrying all navigation, and a floating white card holding just
  // the routed page. Below `lg` the sidebar collapses into `AdminMobileNav`'s
  // hamburger drawer, and the card spans the full width with its own left
  // padding (the sidebar normally supplies that gap on desktop).
  return (
    <ImpersonationFrame>
      <div className="flex h-full bg-canvas">
        <div className="hidden shrink-0 lg:flex">
          <IconRail />
          <Sidebar header={<AdminSidebarHeader />}>
            <AdminSideNav />
          </Sidebar>
        </div>
        <div className="min-w-0 flex-1 py-2 pr-2 pl-2 lg:pl-0">
          <div className="relative flex h-full flex-col overflow-hidden rounded-md bg-background shadow-card">
            <OverlayProvider>
              <AdminMobileNav />
              <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
                <Outlet />
              </main>
            </OverlayProvider>
          </div>
        </div>
      </div>
    </ImpersonationFrame>
  )
}
